import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { getCookieConsent, setCookieConsent, COOKIE_CATEGORIES } from '../utils/cookies';
import { initializeAnalytics, disableAnalytics } from '../utils/analytics';
import Modal from './Modal';
import './CookiePreferences.css';

function CookiePreferences({ isOpen, onClose, requireChoice = false }) {
  const { t } = useTranslation();
  const [preferences, setPreferences] = useState({
    [COOKIE_CATEGORIES.ESSENTIAL]: true, // Always true, can't be disabled
    [COOKIE_CATEGORIES.FUNCTIONAL]: true,
    [COOKIE_CATEGORIES.ANALYTICS]: true
  });

  useEffect(() => {
    if (isOpen) {
      // Load current preferences
      const current = getCookieConsent();
      if (current) {
        setPreferences({
          [COOKIE_CATEGORIES.ESSENTIAL]: true,
          [COOKIE_CATEGORIES.FUNCTIONAL]: current[COOKIE_CATEGORIES.FUNCTIONAL] || false,
          [COOKIE_CATEGORIES.ANALYTICS]: current[COOKIE_CATEGORIES.ANALYTICS] || false
        });
      }
    }
  }, [isOpen]);

  const handleToggle = (category) => {
    // Essential can't be disabled
    if (category === COOKIE_CATEGORIES.ESSENTIAL) return;
    
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleSave = () => {
    setCookieConsent(preferences);
    
    // Handle analytics based on consent
    if (preferences[COOKIE_CATEGORIES.ANALYTICS]) {
      initializeAnalytics();
    } else {
      disableAnalytics();
    }
    
    onClose();
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      [COOKIE_CATEGORIES.ESSENTIAL]: true,
      [COOKIE_CATEGORIES.FUNCTIONAL]: true,
      [COOKIE_CATEGORIES.ANALYTICS]: true
    };
    setCookieConsent(allAccepted);
    initializeAnalytics();
    onClose();
  };

  const handleRejectAll = () => {
    const onlyEssential = {
      [COOKIE_CATEGORIES.ESSENTIAL]: true,
      [COOKIE_CATEGORIES.FUNCTIONAL]: false,
      [COOKIE_CATEGORIES.ANALYTICS]: false
    };
    setCookieConsent(onlyEssential);
    disableAnalytics();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="modal-wide" requireChoice={requireChoice}>
      <div className="cookie-preferences">
        <h2 className="cookie-preferences-title">{t('cookiePreferences.title')}</h2>
        <p className="cookie-preferences-intro">
          <Trans
            i18nKey="cookiePreferences.intro"
            components={{
              1: <Link to="/cookies" className="cookie-preferences-link" />,
            }}
          />
        </p>

        <div className="cookie-preferences-categories">
          <div className="cookie-category">
            <div className="cookie-category-header">
              <div>
                <h3 className="cookie-category-title">{t('cookiePreferences.essentialTitle')}</h3>
                <p className="cookie-category-description">{t('cookiePreferences.essentialBody')}</p>
              </div>
              <div className="cookie-toggle disabled">
                <input
                  type="checkbox"
                  checked={true}
                  disabled={true}
                  readOnly
                />
                <span className="cookie-toggle-label">{t('cookiePreferences.essentialStatus')}</span>
              </div>
            </div>
          </div>

          <div className="cookie-category">
            <div className="cookie-category-header">
              <div>
                <h3 className="cookie-category-title">{t('cookiePreferences.functionalTitle')}</h3>
                <p className="cookie-category-description">{t('cookiePreferences.functionalBody')}</p>
              </div>
              <div className="cookie-toggle">
                <input
                  type="checkbox"
                  checked={preferences[COOKIE_CATEGORIES.FUNCTIONAL]}
                  onChange={() => handleToggle(COOKIE_CATEGORIES.FUNCTIONAL)}
                  id="functional-toggle"
                />
                <label htmlFor="functional-toggle" className="cookie-toggle-label">
                  {preferences[COOKIE_CATEGORIES.FUNCTIONAL] ? t('cookiePreferences.functionalStatusEnabled') : t('cookiePreferences.functionalStatusDisabled')}
                </label>
              </div>
            </div>
          </div>

          <div className="cookie-category">
            <div className="cookie-category-header">
              <div>
                <h3 className="cookie-category-title">{t('cookiePreferences.analyticsTitle')}</h3>
                <p className="cookie-category-description">{t('cookiePreferences.analyticsBody')}</p>
              </div>
              <div className="cookie-toggle">
                <input
                  type="checkbox"
                  checked={preferences[COOKIE_CATEGORIES.ANALYTICS]}
                  onChange={() => handleToggle(COOKIE_CATEGORIES.ANALYTICS)}
                  id="analytics-toggle"
                />
                <label htmlFor="analytics-toggle" className="cookie-toggle-label">
                  {preferences[COOKIE_CATEGORIES.ANALYTICS] ? t('cookiePreferences.analyticsStatusEnabled') : t('cookiePreferences.analyticsStatusDisabled')}
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="cookie-preferences-actions">
          <button
            className="cookie-pref-button cookie-pref-button-secondary"
            onClick={handleRejectAll}
          >
            {t('cookiePreferences.rejectAll')}
          </button>
          <button
            className="cookie-pref-button cookie-pref-button-save"
            onClick={handleSave}
          >
            {t('cookiePreferences.savePreferences')}
          </button>
          <button
            className="cookie-pref-button cookie-pref-button-primary"
            onClick={handleAcceptAll}
          >
            {t('cookiePreferences.acceptAll')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default CookiePreferences;
